import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Card, CardHead } from '@/components/Card';
import { Status } from '@/components/Status';
import { Modal } from '@mantine/core';
import { Icon, type IconName } from '@/components/Icon';
import { useAuth } from '@/store/auth';
import { logoutEverywhere, listSessions, type ActiveSession } from '@/api/auth';
import { usePreferences, type Density } from '@/store/preferences';
import { formatDateTime, initials } from '@/lib/format';
import {
  PURGE_CONFIRMATION,
  createBackup,
  deleteBackup,
  downloadBackupFile,
  listBackups,
  purgeBusinessData,
  restoreBackup,
  uploadBackupFile,
  type Backup,
  type BackupList,
} from '@/api/admin';

type Tab = 'profile' | 'security' | 'preferences' | 'data';

/**
 * Settings page mirroring MAFA's SettingsPage from auth.jsx, ported to
 * a real route at `/settings`. Three tabs — profile, security &
 * session, preferences. Each section is wired to the auth store and
 * the local preferences store; the password change form is rendered
 * but disabled with a hint because the backend hasn't shipped that
 * endpoint yet.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('profile');
  // L'onglet Données n'apparaît que pour un ADMIN. L'API refuse de toute
  // façon, mais une opération destructrice qu'on ne peut pas déclencher n'a
  // rien à faire dans l'interface.
  const isAdmin = useAuth((s) => s.user?.roles.includes('ADMIN') ?? false);

  const tabs: Array<{ key: Tab; label: string; icon: IconName }> = [
    { key: 'profile', label: t('settings.tabs.profile'), icon: 'user' },
    { key: 'security', label: t('settings.tabs.security'), icon: 'shield' },
    { key: 'preferences', label: t('settings.tabs.preferences'), icon: 'sliders' },
    ...(isAdmin
      ? [{ key: 'data' as const, label: t('settings.tabs.data'), icon: 'database' as IconName }]
      : []),
  ];

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <div className="pt">{t('settings.title')}</div>
          <div className="ps">{t('settings.subtitle')}</div>
        </div>
      </div>

      <div className="tabs">
        {tabs.map((tt) => (
          <button key={tt.key} className={tab === tt.key ? 'on' : ''} onClick={() => setTab(tt.key)}>
            <Icon name={tt.icon} />
            {tt.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'preferences' && <PreferencesTab />}
      {tab === 'data' && isAdmin && <DataTab />}
    </div>
  );
}

function ProfileTab() {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const name = user?.displayName ?? user?.email ?? '—';

  return (
    <div className="grid grid-2 fade-in" style={{ maxWidth: 920 }}>
      <Card>
        <CardHead title={t('settings.profile.identity')} />
        <div style={{ padding: 22 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 20,
              paddingBottom: 20,
              borderBottom: '1px solid var(--line)',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: 'var(--accent-tint-2)',
                color: 'var(--accent-ink)',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: 20,
              }}
            >
              {initials(name)}
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{name}</div>
              <div className="muted" style={{ fontWeight: 600, fontSize: 12.5 }}>
                {user?.source ?? '—'} · compte {user?.source === 'LOCAL' ? 'local' : 'fédéré'}
              </div>
            </div>
          </div>
          <div className="field" style={{ marginBottom: 14 }}>
            <label>{t('settings.profile.displayName')}</label>
            <input defaultValue={user?.displayName ?? ''} disabled />
            <div className="hint">Modification bientôt disponible.</div>
          </div>
          <div className="field">
            <label>{t('settings.profile.email')}</label>
            <input defaultValue={user?.email ?? ''} disabled />
          </div>
        </div>
      </Card>

      <Card>
        <CardHead title={t('settings.profile.rolesAccess')} />
        <div style={{ padding: 22 }}>
          <div className="field" style={{ marginBottom: 18 }}>
            <label>{t('settings.profile.rolesBorne')}</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(user?.roles ?? []).map((r) => (
                <span key={r} className="ref">{r}</span>
              ))}
              {(!user?.roles || user.roles.length === 0) && (
                <span className="muted" style={{ fontSize: 13 }}>Aucun rôle</span>
              )}
            </div>
          </div>
          <dl className="kv">
            <dt>{t('settings.profile.modulesAccessible')}</dt>
            <dd>{t('settings.profile.modulesAll')}</dd>
            <dt>{t('settings.profile.accountOrigin')}</dt>
            <dd>{user?.source ?? '—'}</dd>
            <dt>{t('settings.profile.lastLogin')}</dt>
            <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
              {user?.expiresAt ? `Session expire ${formatDateTime(user.expiresAt)}` : '—'}
            </dd>
          </dl>
        </div>
      </Card>
    </div>
  );
}

function SecurityTab() {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const refreshToken = useAuth((s) => s.refreshToken);
  const idleTimeoutSec = useAuth((s) => s.idleTimeoutSec);
  const autoRefresh = usePreferences((s) => s.autoRefresh);
  const setAutoRefresh = usePreferences((s) => s.setAutoRefresh);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);

  // Voir ses propres connexions est le seul moyen de repérer celle qu'on ne
  // reconnaît pas — un poste laissé au bureau, un appareil prêté.
  useEffect(() => {
    if (!refreshToken) return;
    void listSessions().then(setSessions);
  }, [refreshToken]);

  const expiresIn = user?.expiresAt ? remaining(user.expiresAt) : '—';

  return (
    <div className="grid grid-2 fade-in" style={{ maxWidth: 920 }}>
      <Card>
        <CardHead title={t('settings.security.session')} />
        <div style={{ padding: 22 }}>
          <dl className="kv" style={{ marginBottom: 18 }}>
            <dt>{t('settings.security.expiresIn')}</dt>
            <dd>{expiresIn}</dd>
            <dt>{t('settings.security.refresh')}</dt>
            <dd>{autoRefresh ? t('settings.security.refreshAuto') : 'manuel'}</dd>
            <dt>{t('settings.security.idleTimeout')}</dt>
            <dd>{idleTimeoutSec ? `${Math.round(idleTimeoutSec / 60)} min` : '—'}</dd>
          </dl>

          {sessions.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>
                {t('settings.security.openSessions', { count: sessions.length })}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    style={{ padding: '6px 0', borderTop: '1px solid var(--line)' }}
                  >
                    <span style={{ fontWeight: s.current ? 700 : 400 }}>
                      {s.current ? t('settings.security.thisDevice') : (s.ip || '—')}
                    </span>
                    <span className="muted" style={{ marginLeft: 8 }}>
                      {formatDateTime(s.lastSeenAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderTop: '1px solid var(--line)',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                {t('settings.security.autoRefreshTitle')}
              </div>
            </div>
            <button
              type="button"
              className={'toggle' + (autoRefresh ? ' on' : '')}
              onClick={() => setAutoRefresh(!autoRefresh)}
              aria-pressed={autoRefresh}
            >
              <span className="knob" />
            </button>
          </div>
          <Button
            icon="logout"
            onClick={() => void logoutEverywhere()}
            style={{ marginTop: 8 }}
            disabled={refreshToken == null}
          >
            {t('settings.security.logoutAll')}
          </Button>
        </div>
      </Card>

      <Card>
        <CardHead title={t('settings.security.password')} subtitle={t('settings.security.passwordLocal')} />
        <div style={{ padding: 22 }}>
          <div className="field" style={{ marginBottom: 14 }}>
            <label>{t('settings.security.currentPassword')}</label>
            <input type="password" placeholder="••••••••" disabled />
          </div>
          <div className="field" style={{ marginBottom: 14 }}>
            <label>{t('settings.security.newPassword')}</label>
            <input type="password" placeholder="••••••••" disabled />
          </div>
          <div className="field" style={{ marginBottom: 18 }}>
            <label>{t('settings.security.confirmPassword')}</label>
            <input type="password" placeholder="••••••••" disabled />
          </div>
          <Button variant="primary" icon="check" disabled>
            {t('settings.security.updatePassword')}
          </Button>
          <div className="hint" style={{ marginTop: 10 }}>
            {t('settings.security.endpointPending')}
          </div>
        </div>
      </Card>
    </div>
  );
}

function PreferencesTab() {
  const { t } = useTranslation();
  const density = usePreferences((s) => s.density);
  const setDensity = usePreferences((s) => s.setDensity);

  return (
    <div className="card fade-in" style={{ maxWidth: 620 }}>
      <CardHead title={t('settings.preferences.display')} />
      <div style={{ padding: 22 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
            paddingBottom: 20,
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t('settings.preferences.density')}</div>
            <div className="muted" style={{ fontSize: 12, fontWeight: 500 }}>
              {t('settings.preferences.densitySub')}
            </div>
          </div>
          <div className="seg">
            <DensityButton current={density} value="comfortable" onChange={setDensity}>
              {t('settings.preferences.densityComfy')}
            </DensityButton>
            <DensityButton current={density} value="compact" onChange={setDensity}>
              {t('settings.preferences.densityCompact')}
            </DensityButton>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>
              {t('settings.preferences.language')}
            </div>
            <div className="muted" style={{ fontSize: 12, fontWeight: 500 }}>
              {t('settings.preferences.languageSub')}
            </div>
          </div>
          <label className="input">
            <Icon name="users" />
            <select defaultValue="fr" disabled>
              <option value="fr">Français</option>
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}

function DensityButton({
  current,
  value,
  onChange,
  children,
}: {
  current: Density;
  value: Density;
  onChange: (d: Density) => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className={current === value ? 'on' : ''} onClick={() => onChange(value)}>
      {children}
    </button>
  );
}

function remaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 'expirée';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `dans ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `dans ${hours} h ${minutes % 60} min`;
}

/**
 * Onglet Données — remise à zéro des données métier.
 *
 * Placé dans les Réglages plutôt que dans le module Admin : Admin sert à
 * consulter l'exploitation (rejets, audit, dictionnaire), pas à agir sur la
 * base. Une opération irréversible avait sa place auprès des réglages du
 * compte, pas au milieu d'écrans de lecture.
 *
 * Trois obstacles indépendants avant toute destruction : l'onglet n'existe que
 * pour un ADMIN, la phrase doit être recopiée à l'identique, et l'API exige en
 * plus que l'environnement ait été ouvert (MD_DANGER__ALLOW_PURGE).
 */
function DataTab() {
  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <BackupCard />
      <ResetCard />
    </div>
  );
}

/**
 * Sauvegardes — cycle de vie complet.
 *
 * Le téléchargement direct d'autrefois ne laissait aucune trace : le fichier
 * partait dans le navigateur de celui qui avait cliqué, et la plateforme n'en
 * savait rien. On ne pouvait ni le retrouver, ni le rejouer, ni confier un dump
 * venu d'ailleurs.
 *
 * Ici, une sauvegarde est produite SUR LE SERVEUR, inventoriée, et reste
 * disponible au téléchargement comme à la restauration.
 */
function BackupCard() {
  const { t } = useTranslation();
  const accessToken = useAuth((s) => s.accessToken);
  const [data, setData] = useState<BackupList>({ items: [], dir: '', persistent: true });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<Backup | null>(null);

  const refresh = useCallback(async () => {
    setData(await listBackups());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Une production en cours n'a pas de fin prévisible : on interroge la liste
  // tant qu'une ligne est PENDING ou qu'une restauration tourne, puis on cesse.
  const working = data.items.some(
    (b) => b.status === 'PENDING' || b.restoreStatus === 'RUNNING',
  );
  useEffect(() => {
    if (!working) return;
    const id = setInterval(() => void refresh(), 3000);
    return () => clearInterval(id);
  }, [working, refresh]);

  async function onCreate() {
    setBusy('create');
    setError(null);
    setNotice(null);
    const r = await createBackup();
    setBusy(null);
    if (!r.ok) setError(r.message ?? 'échec');
    else setNotice(t('settings.data.backupStarted'));
    void refresh();
  }

  async function onDownload(b: Backup) {
    if (!accessToken) return;
    setBusy(`dl-${b.id}`);
    setError(null);
    const r = await downloadBackupFile(b.id, b.filename, accessToken);
    setBusy(null);
    if (!r.ok) setError(r.message ?? 'échec');
  }

  async function onUpload(file: File) {
    if (!accessToken) return;
    setBusy('upload');
    setError(null);
    setNotice(null);
    const r = await uploadBackupFile(file, accessToken);
    setBusy(null);
    if (!r.ok) setError(r.message ?? 'échec');
    else setNotice(t('settings.data.uploadDone'));
    void refresh();
  }

  async function onRestore(b: Backup) {
    setConfirmRestore(null);
    setBusy(`rs-${b.id}`);
    setError(null);
    const r = await restoreBackup(b.id);
    setBusy(null);
    if (!r.ok) setError(r.message ?? 'échec');
    else setNotice(t('settings.data.restoreStarted'));
    void refresh();
  }

  async function onDelete(b: Backup) {
    setBusy(`rm-${b.id}`);
    await deleteBackup(b.id);
    setBusy(null);
    void refresh();
  }

  return (
    <Card>
      <CardHead
        title={t('settings.data.backupTitle')}
        subtitle={t('settings.data.backupSubtitle')}
      />
      <div style={{ padding: 22 }}>
        <dl className="kv" style={{ marginBottom: 18 }}>
          <dt>{t('settings.data.backupWhat')}</dt>
          <dd>{t('settings.data.backupWhatList')}</dd>
          <dt>{t('settings.data.backupFormat')}</dt>
          <dd>{t('settings.data.backupFormatList')}</dd>
          <dt>{t('settings.data.backupRestore')}</dt>
          <dd>{t('settings.data.backupRestoreList')}</dd>
        </dl>

        {!data.persistent && data.dir && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px 14px',
              marginBottom: 16,
              borderRadius: 10,
              background: 'var(--alert-tint)',
              color: 'var(--alert-ink)',
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            <Icon name="alert" />
            <span>{t('settings.data.volumeEphemeral', { dir: data.dir })}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Button
            variant="primary"
            icon="database"
            loading={busy === 'create'}
            disabled={busy !== null}
            onClick={onCreate}
          >
            {t('settings.data.backupSubmit')}
          </Button>
          <label>
            <input
              type="file"
              accept=".gz,.ndjson.gz,application/gzip"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void onUpload(f);
              }}
            />
            <Button
              icon="upload"
              loading={busy === 'upload'}
              disabled={busy !== null}
              onClick={(e) => {
                const input = (e.currentTarget.parentElement as HTMLLabelElement)
                  .querySelector('input');
                input?.click();
              }}
            >
              {t('settings.data.uploadSubmit')}
            </Button>
          </label>
        </div>

        {error && (
          <div className="hint" style={{ marginBottom: 10, color: 'var(--alert-ink)' }}>
            {error}
          </div>
        )}
        {notice && <div className="hint" style={{ marginBottom: 10 }}>{notice}</div>}

        {data.items.length === 0 ? (
          <div className="hint">{t('settings.data.noBackup')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ink-2)' }}>
                <th style={{ padding: '8px 6px' }}>{t('settings.data.colFile')}</th>
                <th style={{ padding: '8px 6px' }}>{t('settings.data.colState')}</th>
                <th style={{ padding: '8px 6px' }}>{t('settings.data.colSize')}</th>
                <th style={{ padding: '8px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {data.items.map((b) => (
                <tr key={b.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: '10px 6px' }}>
                    <div style={{ fontWeight: 600 }}>{b.filename}</div>
                    <div className="hint">
                      {formatDateTime(b.createdAt)}
                      {b.actorLabel ? ` · ${b.actorLabel}` : ''}
                      {b.origin === 'UPLOADED' ? ` · ${t('settings.data.uploaded')}` : ''}
                    </div>
                  </td>
                  <td style={{ padding: '10px 6px' }}>
                    <BackupState backup={b} />
                  </td>
                  <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                    {b.sizeBytes ? `${(b.sizeBytes / (1024 * 1024)).toFixed(1)} Mo` : '—'}
                    {b.counts?.event != null && (
                      <div className="hint">
                        {b.counts.event.toLocaleString('fr-FR')} évén.
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Button
                      size="sm"
                      icon="download"
                      loading={busy === `dl-${b.id}`}
                      disabled={b.status !== 'COMPLETED' || b.fileMissing || busy !== null}
                      onClick={() => void onDownload(b)}
                    >
                      {t('settings.data.download')}
                    </Button>{' '}
                    <Button
                      size="sm"
                      icon="refresh"
                      loading={busy === `rs-${b.id}`}
                      disabled={b.status !== 'COMPLETED' || b.fileMissing || busy !== null}
                      onClick={() => setConfirmRestore(b)}
                    >
                      {t('settings.data.restore')}
                    </Button>{' '}
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="trash"
                      loading={busy === `rm-${b.id}`}
                      disabled={busy !== null}
                      onClick={() => void onDelete(b)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        opened={confirmRestore != null}
        onClose={() => setConfirmRestore(null)}
        title={t('settings.data.restoreTitle')}
        size={520}
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>
            {t('settings.data.restoreExplain')}
          </p>
          <code style={{ fontSize: 12.5 }}>{confirmRestore?.filename}</code>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setConfirmRestore(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              icon="refresh"
              onClick={() => confirmRestore && void onRestore(confirmRestore)}
            >
              {t('settings.data.restore')}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

/** État lisible d'une sauvegarde : production, restauration, fichier manquant. */
function BackupState({ backup: b }: { backup: Backup }) {
  const { t } = useTranslation();
  if (b.status === 'PENDING') return <Status kind="warn">{t('settings.data.statePending')}</Status>;
  if (b.status === 'FAILED')
    return (
      <div>
        <Status kind="warn">{t('settings.data.stateFailed')}</Status>
        {b.errorMessage && <div className="hint">{b.errorMessage}</div>}
      </div>
    );
  if (b.fileMissing) return <Status kind="warn">{t('settings.data.stateMissing')}</Status>;
  return (
    <div>
      <Status kind="ok">{t('settings.data.stateReady')}</Status>
      {b.restoreStatus === 'RUNNING' && (
        <div className="hint">{t('settings.data.restoreRunning')}</div>
      )}
      {b.restoreStatus === 'COMPLETED' && (
        <div className="hint">
          {t('settings.data.restoreDone', { n: b.restoredEvents ?? 0 })}
        </div>
      )}
      {b.restoreStatus === 'FAILED' && (
        <div className="hint" style={{ color: 'var(--alert-ink)' }}>
          {b.restoreError}
        </div>
      )}
    </div>
  );
}

function ResetCard() {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [includeActors, setIncludeActors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const armed = typed.trim() === PURGE_CONFIRMATION && !busy;

  async function run() {
    if (!armed) return;
    setBusy(true);
    setError(null);
    const r = await purgeBusinessData(typed.trim(), includeActors);
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setTyped('');
    setDone(
      t('settings.data.doneSummary', {
        entrepreneurs: r.result.entrepreneurs,
        events: r.result.events,
        imports: r.result.imports,
      }),
    );
  }

  return (
    <Card style={{ borderColor: 'var(--alert-tint-2)' }}>
      <CardHead
        title={t('settings.data.resetTitle')}
        subtitle={t('settings.data.resetSubtitle')}
      />
      <div style={{ padding: 22 }}>
        <dl className="kv" style={{ marginBottom: 18 }}>
          <dt>{t('settings.data.erased')}</dt>
          <dd>{t('settings.data.erasedList')}</dd>
          <dt>{t('settings.data.kept')}</dt>
          <dd>{t('settings.data.keptList')}</dd>
        </dl>

        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: '12px 14px',
            marginBottom: 18,
            borderRadius: 10,
            background: 'var(--alert-tint)',
            color: 'var(--alert-ink)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <Icon name="alert" />
          <span>{t('settings.data.warning')}</span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '12px 0',
            marginBottom: 14,
            borderTop: '1px solid var(--line)',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>
              {t('settings.data.includeActors')}
            </div>
            <div className="hint">{t('settings.data.includeActorsHint')}</div>
          </div>
          <button
            type="button"
            className={'toggle' + (includeActors ? ' on' : '')}
            onClick={() => setIncludeActors(!includeActors)}
            aria-pressed={includeActors}
          >
            <span className="knob" />
          </button>
        </div>

        <div className="field" style={{ marginBottom: 14 }}>
          <label>{t('settings.data.confirmLabel')}</label>
          <code
            style={{
              display: 'block',
              marginBottom: 7,
              fontSize: 12.5,
              color: 'var(--alert-ink)',
              userSelect: 'all',
            }}
          >
            {PURGE_CONFIRMATION}
          </code>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={t('settings.data.confirmPlaceholder')}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <Button variant="alert" icon="trash" disabled={!armed} loading={busy} onClick={run}>
          {busy ? t('settings.data.submitting') : t('settings.data.submit')}
        </Button>

        {error && (
          <div className="hint" style={{ marginTop: 10, color: 'var(--alert-ink)' }}>
            {error}
          </div>
        )}
        {done && <div className="hint" style={{ marginTop: 10 }}>{done}</div>}
        {!error && !done && (
          <div className="hint" style={{ marginTop: 10 }}>
            {t('settings.data.backupBeforeReset')} {t('settings.data.closedEnv')}
          </div>
        )}
      </div>
    </Card>
  );
}
